/**
 * ============================================================================
 * CANONICAL COLOUR SOURCE OF TRUTH
 * ============================================================================
 *
 * File: src/theme/tokens/colors.js
 *
 * ALL application colours must originate here.
 * Do NOT introduce independent hex/rgb values in:
 *   - CSS files (use derived CSS variables)
 *   - GenericThemeColors (re-exports only)
 *   - src/colors/colors.js (legacy re-exports only)
 *   - Components (future migration — use theme.palette / CSS vars)
 *
 * Consumers (all DERIVED from this file):
 *   1. createAppTheme()          → MUI palette
 *   2. cssColorVariables.js      → --color-* custom properties
 *   3. flatColors / compactColors → legacy flat APIs (light mode only)
 *   4. src/colors/colors.js      → legacy named constants
 *   5. GenericThemeColors        → re-export facade
 *
 * To change brand colours: edit lightColors / darkColors below only.
 * ============================================================================
 */

/**
 * @typedef {Object} ColorPalette
 * @property {'light'|'dark'} mode
 * @property {{ main: string, dark: string, light: string, contrastText: string }} primary
 * @property {{ main: string, dark: string, light: string, contrastText: string }} secondary
 * @property {{ default: string, paper: string }} background
 * @property {{ primary: string, secondary: string, disabled: string }} text
 * @property {{
 *   success: string, successDark: string,
 *   warning: string, warningDark: string,
 *   error: string, errorDark: string,
 *   info: string, infoDark: string
 * }} status
 * @property {{ background: string, border: string, borderDark: string, dark: string }} grey
 * @property {{ black: string, white: string }} common
 * @property {{ hover: string, selected: string }} action
 * @property {{
 *   sidebarBg: string, fulfilled: string, unfulfilled: string,
 *   divider?: string, breadcrumbText: string, mars: string, lightGrey: string, blackLegacy: string
 * }} custom
 * @property {{
 *   glassBg: string, glassHover: string,
 *   chipBg: string, chipText: string, chipBorder: string,
 *   secondaryGlow: string, shadow: string
 * }} effects
 */

/** @type {{ palette: ColorPalette }} */
export const lightColors = {
  palette: {
    mode: 'light',

    primary: {
      main: '#00796B',
      dark: '#004D40',
      light: '#4DB6AC',
      contrastText: '#FFFFFF',
    },

    secondary: {
      main: '#FF6F00',
      dark: '#E65100',
      light: '#FF8F00',
      contrastText: '#FFFFFF',
    },

    background: {
      default: '#FFFFFF',
      paper: '#F8F9FA',
    },

    text: {
      primary: '#343a40',
      secondary: '#4f4f4f',
      disabled: '#9E9E9E',
    },

    status: {
      // Semantic status — distinct roles (edit here only)
      success: '#2E7D32',
      successDark: '#1B5E20',
      warning: '#FF6F00',
      warningDark: '#E65100',
      error: '#D32F2F',
      errorDark: '#C62828',
      info: '#0288D1',
      infoDark: '#01579B',
    },

    grey: {
      background: '#F5F5F5',
      border: '#E0E0E0',
      borderDark: '#BDBDBD',
      dark: '#757575',
    },

    common: {
      black: '#000000',
      white: '#FFFFFF',
    },

    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
    },

    custom: {
      sidebarBg: '#34495E',
      fulfilled: '#4CAF50',
      unfulfilled: '#F44336',
      divider: 'rgba(0, 0, 0, 0.12)',
      breadcrumbText: '#BDC3C7',
      // Legacy named constants (still owned by this SSoT)
      mars: '#134b50',
      lightGrey: '#8d8d95',
      blackLegacy: '#0c0a0a',
    },

    effects: {
      glassBg: 'rgba(255, 255, 255, 0.7)',
      glassHover: 'rgba(255, 255, 255, 0.85)',
      chipBg: '#F8F9FA',
      chipText: '#495057',
      chipBorder: '#DEE2E6',
      secondaryGlow: 'rgba(0, 121, 107, 0.15)',
      shadow: 'rgba(0, 0, 0, 0.08)',
    },
  },
};

/** @type {{ palette: ColorPalette }} */
export const darkColors = {
  palette: {
    mode: 'dark',

    primary: {
      main: '#80cbc4',
      dark: '#4db6ac',
      light: '#B2DFDB',
      contrastText: '#FFFFFF',
    },

    secondary: {
      main: '#FF7043',
      dark: '#F4511E',
      light: '#FF8A65',
      contrastText: '#000000',
    },

    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },

    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
      disabled: '#6E6E6E',
    },

    status: {
      success: '#66BB6A',
      successDark: '#388E3C',
      warning: '#FFA726',
      warningDark: '#F57C00',
      error: '#EF5350',
      errorDark: '#C62828',
      info: '#29B6F6',
      infoDark: '#0288D1',
    },

    grey: {
      background: '#2C2C2C',
      border: '#3E3E3E',
      borderDark: '#2A2A2A',
      dark: '#757575',
    },

    common: {
      black: '#000000',
      white: '#FFFFFF',
    },

    action: {
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
    },

    custom: {
      sidebarBg: '#1A1A1A',
      fulfilled: '#66BB6A',
      unfulfilled: '#EF5350',
      divider: 'rgba(255, 255, 255, 0.12)',
      breadcrumbText: '#BDC3C7',
      mars: '#134b50',
      lightGrey: '#8d8d95',
      blackLegacy: '#0c0a0a',
    },

    effects: {
      glassBg: 'rgba(255, 255, 255, 0.05)',
      glassHover: 'rgba(255, 255, 255, 0.1)',
      chipBg: 'rgba(255, 255, 255, 0.05)',
      chipText: '#E0E0E0',
      chipBorder: '#424242',
      secondaryGlow: 'rgba(128, 203, 196, 0.2)',
      shadow: 'rgba(0, 0, 0, 0.5)',
    },
  },
};

/**
 * Resolve canonical palette for a mode.
 * @param {'light'|'dark'} [mode='light']
 */
export const getColorPalette = (mode = 'light') =>
  (mode === 'dark' ? darkColors : lightColors).palette;

// ---------------------------------------------------------------------------
// Legacy named constants — DERIVED from lightColors (not independent values)
// ---------------------------------------------------------------------------

export const WHITE = lightColors.palette.common.white;
export const BLACK = lightColors.palette.custom.blackLegacy;
export const LIGHT_GREY = lightColors.palette.custom.lightGrey;
export const MARS = lightColors.palette.custom.mars;
export const SKY_BLUE = lightColors.palette.primary.main;

/**
 * Flat colour object — DERIVED from lightColors (light mode only).
 *
 * @deprecated Dark-mode unsafe. Legacy only — do NOT use in new code.
 * Prefer theme.palette.* (useTheme) or CSS vars var(--color-*).
 * Will be removed after CreateOrders / orderStyles migration.
 */
export const flatColors = {
  primary: lightColors.palette.primary.main,
  primaryDark: lightColors.palette.primary.dark,
  primaryLight: lightColors.palette.primary.light,

  success: lightColors.palette.status.success,
  warning: lightColors.palette.status.warning,
  error: lightColors.palette.status.error,
  errorDark: lightColors.palette.status.errorDark,

  surface: lightColors.palette.background.paper,
  background: lightColors.palette.background.default,
  backgroundLight: lightColors.palette.grey.background,

  border: lightColors.palette.grey.border,
  borderDark: lightColors.palette.grey.borderDark,

  text: {
    primary: lightColors.palette.text.primary,
    secondary: lightColors.palette.text.secondary,
    muted: lightColors.palette.text.disabled,
    white: lightColors.palette.common.white,
  },

  custom: lightColors.palette.custom,

  headerBg: lightColors.palette.primary.main,
  topBar: lightColors.palette.primary.main,

  WHITE,
  BLACK,
  LIGHT_GREY,
  SKY_BLUE,
  MARS,
};

/**
 * Compact colour object — DERIVED from lightColors / flatColors.
 *
 * @deprecated Dark-mode unsafe. Legacy only — do NOT use in new code.
 * Prefer theme.palette.* (useTheme) or CSS vars var(--color-*).
 * Will be removed after CreateOrders / orderStyles migration.
 */
export const compactColors = {
  primary: flatColors.primary,
  primaryDark: flatColors.primaryDark,
  primaryLight: flatColors.primaryLight,
  secondary: lightColors.palette.grey.dark,

  success: flatColors.success,
  warning: flatColors.warning,
  error: flatColors.error,
  info: lightColors.palette.status.info,
  mars: flatColors.MARS,

  background: flatColors.background,
  backgroundLight: flatColors.backgroundLight,
  surface: flatColors.surface,

  border: flatColors.border,
  hover: lightColors.palette.action.hover,
  active: lightColors.palette.action.selected,
  shadow: lightColors.palette.effects.shadow,

  text: {
    primary: flatColors.text.primary,
    secondary: flatColors.text.secondary,
    muted: flatColors.text.muted,
    white: flatColors.text.white,
  },

  headerBg: flatColors.headerBg,
  headerText: lightColors.palette.common.white,
  breadcrumbText: lightColors.palette.custom.breadcrumbText,
};

/**
 * Default `colors` export — DERIVED flat (light) API for legacy imports.
 * Canonical structured data remains lightColors / darkColors.
 */
export const colors = flatColors;

export default lightColors;
